/**
 * Os seis estados de tela. O desenho é feito uma vez e reusado nas oito telas.
 *
 * O que todos têm em comum: **nenhum deles desenha zero no lugar do que não existe**. Na tela
 * degradada os contadores vêm zerados pela API, e esses zeros não são dado — a tela mostra o banner
 * e esconde as listas em vez de afirmar "0 enviados".
 */
import type { ReactNode } from 'react';
import { AlertTriangle, CloudOff, Inbox, Lock, RefreshCw, Clock } from 'lucide-react';
import { Skel } from '@/components/v2/ui';
import { c, v } from '@/components/v2/theme';
import { EmailApiError } from '@/services/api/email-v1';
import type { EmailUnavailable } from '@/types/email-v1';
import { missingDayLabel } from '../lib/format';

/* ----------------------------------------------------------------- casca */

function Center({ icon, title, children, action }: { icon: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 10, textAlign: 'center', padding: '56px 24px', background: v('surface'),
            border: `1px solid ${v('border')}`, borderRadius: 18,
        }}>
            {icon}
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
            {children && <p style={{ margin: 0, maxWidth: 460, fontSize: 13, lineHeight: 1.55, color: v('muted') }}>{children}</p>}
            {action}
        </div>
    );
}

function Retry({ onRetry, label = 'Recarregar' }: { onRetry: () => void; label?: string }) {
    return (
        <button type="button" onClick={onRetry} className="rv-h-bd"
            style={{
                marginTop: 4, height: 34, padding: '0 14px', display: 'inline-flex', alignItems: 'center', gap: 8,
                borderRadius: 10, border: `1px solid ${v('border-2')}`, background: v('inset'), color: v('text'),
                font: 'inherit', fontSize: 13, cursor: 'pointer',
            }}>
            <RefreshCw size={14} />{label}
        </button>
    );
}

/* ----------------------------------------------------------------- 1. carregando */

/** Esqueleto da estrutura real — a tela pode levar até ~30 s, e o formato já antecipa o que vem. */
export function LoadingState({ rows = 6, chart = false }: { rows?: number; chart?: boolean }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} aria-busy="true" aria-live="polite">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,200px),1fr))', gap: 12 }}>
                {[0, 1, 2, 3].map((i) => <Skel key={i} h={96} />)}
            </div>
            {chart && <Skel h={320} r={18} />}
            <div style={{ background: v('surface'), border: `1px solid ${v('border')}`, borderRadius: 18, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Skel h={18} w={220} r={8} />
                {Array.from({ length: rows }, (_, i) => <Skel key={i} h={38} r={10} />)}
            </div>
            <span style={{ fontSize: 12, color: v('faint'), textAlign: 'center' }}>
                Lendo os envios ao vivo — pode levar alguns segundos.
            </span>
        </div>
    );
}

/* ----------------------------------------------------------------- 2. degradado */

const WHY: Record<EmailUnavailable, string> = {
    core_unavailable: 'o sistema de e-mail não respondeu',
    report_timeout: 'a leitura dos envios demorou mais que o limite',
};

/**
 * Banner da tela degradada. A receita continua visível porque vem do GAM, que respondeu; o que falta
 * é o lado dos envios. Fica no topo do conteúdo, e as listas não são desenhadas.
 */
export function DegradedBanner({ reason, onRetry }: { reason: EmailUnavailable; onRetry: () => void }) {
    return (
        <div role="status" style={{
            display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 16px', borderRadius: 14,
            border: `1px solid ${c('amber', 0.35)}`, background: c('amber', 0.08), fontSize: 13, lineHeight: 1.55,
        }}>
            <CloudOff size={17} color={c('amber')} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontWeight: 600 }}>Envios não lidos:</strong> {WHY[reason]}. A receita abaixo é real;
                os contadores de envio não puderam ser lidos e <strong style={{ fontWeight: 600 }}>não estão sendo
                mostrados como zero</strong>.
            </div>
            <Retry onRetry={onRetry} label="Tentar de novo" />
        </div>
    );
}

/* ----------------------------------------------------------------- 3. receita não carregada */

/** O 503 da API: não é defeito, é espera. Acontece só nas telas que usam receita. */
export function PendingLoadState({ detail, missing, onRetry }: { detail: string; missing: string[]; onRetry: () => void }) {
    const days = missing.map(missingDayLabel);
    return (
        <Center
            icon={<Clock size={22} color={c('blue')} />}
            title={detail || 'Os dados deste período ainda não foram carregados'}
            action={<Retry onRetry={onRetry} />}
        >
            {days.length
                ? <>Faltam {days.length === 1 ? 'os dados de' : 'os dados dos dias'} <strong style={{ fontWeight: 600 }}>{days.join(', ')}</strong>. A carga roda sozinha — tente de novo em alguns minutos, ou escolha um período que já esteja carregado.</>
                : <>A carga roda sozinha. Tente de novo em alguns minutos, ou escolha um período anterior.</>}
        </Center>
    );
}

/* ----------------------------------------------------------------- 4. vazio de verdade */

export function EmptyState({ what, onWiden }: { what: string; onWiden?: () => void }) {
    return (
        <Center icon={<Inbox size={22} color={v('faint')} />} title={`Nenhum ${what} no período`}
            action={onWiden && <Retry onRetry={onWiden} label="Ver os últimos 30 dias" />}>
            O filtro não encontrou nada nesta janela. Amplie o período ou troque o projeto.
        </Center>
    );
}

/* ----------------------------------------------------------------- 5. sem projeto de e-mail */

export function NoEmailProjectState() {
    return (
        <Center icon={<AlertTriangle size={22} color={c('amber')} />} title="Nenhum projeto de e-mail no seu acesso">
            Você tem projeto no sistema, mas nenhum deles está ligado ao sistema de e-mail — são cadastros
            diferentes. Não é falha de leitura: não há o que mostrar aqui até que um projeto de e-mail seja
            vinculado ao seu acesso.
        </Center>
    );
}

/* ----------------------------------------------------------------- 6. sem permissão */

export function ForbiddenState({ detail }: { detail?: string }) {
    return (
        <Center icon={<Lock size={22} color={c('bad')} />} title="Acesso negado">
            {detail || 'Você não tem a permissão necessária para ver os relatórios de e-mail. Peça acesso a quem administra sua conta.'}
        </Center>
    );
}

/* ----------------------------------------------------------------- erro genérico */

export function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
    const detail = error instanceof EmailApiError ? error.detail : 'Não foi possível carregar esta tela.';
    return (
        <Center icon={<AlertTriangle size={22} color={c('bad')} />} title="Não deu para carregar" action={<Retry onRetry={onRetry} />}>
            {detail}
        </Center>
    );
}
